import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useImperativeHandle, useRef, } from "react";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise = null;
const loadScript = () => {
    scriptPromise ??= new Promise((resolve, reject) => {
        if (window.turnstile)
            return resolve();
        const script = document.createElement("script");
        script.src = SCRIPT_URL;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("turnstile failed to load"));
        document.head.appendChild(script);
    });
    return scriptPromise;
};
/**
 * Managed widget in execute mode: invisible for almost every respondent, and
 * escalating to a checkbox only when Cloudflare's signal is bad.
 *
 * Tokens are single-use, so `reset()` must be called after any failed submit
 * or the retry reuses a spent token and fails as timeout-or-duplicate.
 */
export const Turnstile = ({ siteKey, ref, }) => {
    const container = useRef(null);
    const widgetId = useRef(null);
    const pending = useRef(null);
    useEffect(() => {
        let cancelled = false;
        void loadScript()
            .then(() => {
            if (cancelled || !container.current || !window.turnstile)
                return;
            widgetId.current = window.turnstile.render(container.current, {
                sitekey: siteKey,
                size: "flexible",
                appearance: "interaction-only",
                callback: (token) => {
                    pending.current?.(token);
                    pending.current = null;
                },
                "error-callback": () => {
                    pending.current?.(null);
                    pending.current = null;
                },
            });
        })
            .catch(() => {
            // Submitting without a token gets a clear 400 from the API, which is a
            // better failure than blocking the form on a third-party script.
        });
        return () => {
            cancelled = true;
        };
    }, [siteKey]);
    const getToken = useCallback(() => new Promise((resolve) => {
        if (!window.turnstile || widgetId.current === null)
            return resolve(null);
        pending.current = resolve;
        window.turnstile.execute(widgetId.current);
        // Never leave a submit hanging on a widget that will not answer.
        setTimeout(() => {
            if (pending.current) {
                pending.current(null);
                pending.current = null;
            }
        }, 15000);
    }), []);
    const reset = useCallback(() => {
        if (window.turnstile && widgetId.current !== null) {
            window.turnstile.reset(widgetId.current);
        }
    }, []);
    useImperativeHandle(ref, () => ({ getToken, reset }), [getToken, reset]);
    return _jsx("div", { ref: container, className: "mt-4" });
};
//# sourceMappingURL=Turnstile.js.map